import Modal from 'flarum/common/components/Modal';
import Api from '../utils/Api';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason } from '../utils/FormPrimitives';

/**
 * TrialDetailModal — 「体验详情」弹层 (v0.17.0, 辉哥 10/12 拍板)
 *
 * 回答一个问题: **这个手机号的人, 体验过几次课, 每次上的是什么。**
 *
 * ## 归并键 = 手机号 (规格硬要求)
 *   同一号码约 N 次体验 → sales_followups 里只有 1 条记录 (is_trial=1),
 *   课挂在 lessons.sales_followup_id 上。这个弹层把那一串课一次性摊开。
 *
 * ## 倒序 + 含已取消 (两个都是规格硬要求)
 *   ① 倒序: 销售最关心最近那次, 不是三个月前那次
 *   ② 已取消**保留**: 业务诉求是回溯「这号人来过几次、为什么没成」。
 *      过滤掉取消记录 = 销售看到「来过 1 次」而客户其实来过 3 次 2 次没成,
 *      漏掉的正是最该看见的信息。取消行用**降饱和**表达, 不是隐藏。
 *
 * ## 每条 5 个字段 (辉哥明确要求, 一个都不能少)
 *   体验时间 / 课程分类 / 教练 / 教室 / 体验卡
 *   课程分类是本轮唯一硬性新增 —— 一个人可能跨分类试课 (羽毛球→网球),
 *   不显示分类就看不出他到底对什么感兴趣。
 */
export default class TrialDetailModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.followup = vnode.attrs.followup || {};
    this.loading = true;
    this.data = null;
    this.error = '';
    this.load();
  }
  className() {
    // 跟 SalesFollowupCreateModal 同款: 桌面居中 + 移动全屏
    return 'Modal--medium Modal--fullscreen-padded ZS-TrialDetailModal';
  }
  title() {
    return m("span", null, m("i", {
      className: "fas fa-clock-rotate-left",
      style: "color:var(--zs-purple-a78bfa, #a78bfa);margin-right:8px;"
    }), "\u4F53\u9A8C\u8BE6\u60C5");
  }
  async load() {
    try {
      this.loading = true;
      m.redraw();
      this.data = await Api.listTrialLessons(this.followup.id);
    } catch (e) {
      console.error('[trial-detail] load failed', e);
      // v0.17.65 — 为什么不能只看 `e.message` (改前这里只有 `|| '加载失败'`)
      //   后端抛 ValidationException 时 HTTP 是 422, **真实原因在 `e.response.errors[0].detail`**;
      //   `e.message` 只是请求层拼的 "Response code 422 (Unprocessable Entity)", 跟后端那句提示无关。
      //   后果: 体验课详情拉不到时只显示「加载失败」, 销售看不出是**无权限**还是**跟进单已删**还是**参数非法**,
      //   没法判断该换单据还是该找管理员开权限。
      //   形状照抄同仓既有正确写法 (CalendarTab.js:2770 / modal/FixedLessonModal.js:593);
      //   兜底文案「加载失败」保留不变, 只是降为最后一档 (网络层异常 / 非 422 时仍命中)。
      this.error = errReason(e, '加载失败');
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  /** '2026-10-13 16:00' → '2026-10-13 周二 · 16:00–17:00' */
  formatRow(t) {
    const wd = ['日', '一', '二', '三', '四', '五', '六'];
    const s = String(t.start_at || '');
    const e = String(t.end_at || '');
    if (!s) return '—';
    const date = s.slice(0, 10);
    const st = s.slice(11, 16);
    const et = e.slice(11, 16);
    let dayMark = '';
    const d = new Date(date + 'T00:00:00');
    if (!isNaN(d.getTime())) dayMark = ' 周' + wd[d.getDay()];
    return et && et !== st ? `${date}${dayMark} · ${st}–${et}` : `${date}${dayMark} · ${st}`;
  }

  /** 状态角标: 已约课(蓝) / 已完成(绿) / 已取消(灰) */
  stateBadge(state) {
    if (state === 'cancelled') return m("span", {
      className: "ZS-td-tag gray"
    }, "\u5DF2\u53D6\u6D88");
    if (state === 'completed') return m("span", {
      className: "ZS-td-tag green"
    }, "\u5DF2\u5B8C\u6210");
    return m("span", {
      className: "ZS-td-tag blue"
    }, "\u5DF2\u7EA6\u8BFE");
  }
  renderRow(t) {
    const cancelled = t.state === 'cancelled';
    const dim = cancelled ? ' is-cancelled' : '';
    return m("div", {
      className: 'ZS-td-row' + dim,
      key: t.id
    }, m("div", {
      className: "ZS-td-rowhead"
    }, m("span", {
      className: 'ZS-td-time' + (cancelled ? ' strike' : '')
    }, this.formatRow(t)), this.stateBadge(t.state)), m("div", {
      className: "ZS-td-fields"
    }, m("div", {
      className: "ZS-td-f"
    }, m("em", null, "\u8BFE\u7A0B\u5206\u7C7B"), m("span", null, t.lesson_category_name || '—')), m("div", {
      className: "ZS-td-f"
    }, m("em", null, "\u6559\u7EC3"), m("span", null, t.coach_name || '—')), m("div", {
      className: "ZS-td-f"
    }, m("em", null, "\u6559\u5BA4"), m("span", null, t.room_name || '—')), m("div", {
      className: "ZS-td-f"
    }, m("em", null, "\u4F53\u9A8C\u5361"), m("span", null, t.card_name || '—'))), cancelled && m("div", {
      className: "ZS-td-note"
    }, "\u672C\u6B21\u4F53\u9A8C\u672A\u6D88\u8017\u4F53\u9A8C\u5361\uFF0C\u8DDF\u8FDB\u9636\u6BB5\u5DF2\u9000\u56DE\u300C\u5DF2\u8054\u7CFB\u300D"));
  }
  content() {
    const d = this.data;
    const rows = d && d.trials || [];
    const name = d && d.customer_name || this.followup.customer_name || '—';
    const phone = d && d.phone || this.followup.phone || '—';
    return m("div", null, m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "ZS-td-head"
    }, m("div", {
      className: "ZS-td-who"
    }, m("span", {
      className: "nm"
    }, name), m("span", {
      className: "ph"
    }, phone), m("span", {
      className: "ZS-td-count"
    }, "\u5171 ", rows.length, " \u6B21\u4F53\u9A8C")), m("div", {
      className: "ZS-td-desc"
    }, "\u540C\u4E00\u624B\u673A\u53F7 ", phone, " \u7684\u5168\u90E8\u4F53\u9A8C\u9884\u7EA6\uFF0C\u6700\u8FD1\u4E00\u6B21\u5728\u6700\u4E0A\uFF1B\u5DF2\u53D6\u6D88\u7684\u4E5F\u4FDD\u7559\u5728\u5217\u8868\u91CC\u3002")), this.loading && m("div", {
      style: "padding:30px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u52A0\u8F7D\u4E2D..."), !this.loading && this.error && m("div", {
      className: "ZS-td-note",
      style: "color:var(--zs-danger-ef4444, #ef4444);"
    }, this.error), !this.loading && !this.error && rows.length === 0 && m("div", {
      style: "padding:30px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u8BE5\u5BA2\u6237\u8FD8\u6CA1\u6709\u4F53\u9A8C\u9884\u7EA6\u8BB0\u5F55"), !this.loading && !this.error && rows.length > 0 && m("div", {
      className: "ZS-td-list"
    }, rows.map(t => this.renderRow(t))), m("div", {
      className: "ZS-td-foot"
    }, "\u4F53\u9A8C\u8BB0\u5F55\u53EA\u8BFB\uFF0C\u6539\u7EA6\u6216\u53D6\u6D88\u8BF7\u5230\u8BFE\u8868\u91CC\u64CD\u4F5C\uFF1B\u6B64\u5904\u4EC5\u7528\u4E8E\u56DE\u6EAF\u8FD9\u53F7\u4EBA\u4E0A\u8FC7\u4EC0\u4E48\u8BFE\u3002")), m("div", {
      className: "Modal-footer"
    }, m("button", {
      className: "Button Button--primary",
      onclick: () => this.hide()
    }, "\u5173\u95ED")));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/TrialDetailModal', TrialDetailModal);